import { UTCDate } from '@date-fns/utc';
import { EvidenceStatus, OAuthProvider, TransactionType } from '@seapoint/server/enums';
import * as Sentry from '@sentry/react';
import { format } from 'date-fns';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ComponentProps, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import useSWR from 'swr';
import useSWRImmutable from 'swr/immutable';
import useSWRMutation from 'swr/mutation';
import { useData } from '../../contexts/data-context/use-data';
import { ErrorSection } from '../../design-system/error-section/error-section';
import { LoadingSection } from '../../design-system/loading/loading';
import { InvoiceEvidenceView } from '../../types/entity-types';
import { deleteSwrFetcherWithDynamicUrl } from '../../utils/delete-swr-fetcher-with-dynamic-url';
import { exhaustiveCheck } from '../../utils/exhaustive-check';
import { getAmountWithCurrency } from '../../utils/get-amount-with-currency';
import { getSwrFetcher } from '../../utils/get-swr-fetcher';
import { getSwrFetcherWithDynamicUrl } from '../../utils/get-swr-fetcher-with-dynamic-url';
import { postSwrFetcher } from '../../utils/post-swr-fetcher';
import { postSwrFetcherWithDynamicUrl } from '../../utils/post-swr-fetcher-with-dynamic-url';
import { putSwrFetcherWithDynamicUrl } from '../../utils/put-swr-fetcher-with-dynamic-url';
import { useEntityModalState } from '../../utils/use-entity-modal-state';
import { IS_XERO_CARD_HIDDEN_KEY } from '../payments/components/payments-flow-modal';
import { isSupportedCurrency } from '../payments/utils/is-supported-currency';
import { TransactionDetailsDrawerWrapper } from '../transactions/components/transaction-details-drawer/transaction-details-drawer-wrapper';
import { TransactionModalView } from '../transactions/components/transaction-table-wrapper';
import { EmailConnectionModal } from './components/email-connection-modal/email-connection-modal';
import { MenuItemVariant } from './components/invoice-data-section/invoice-table-row-options-menu';
import { ExcludeSenderModal } from './components/invoice-data-section/modals/exclude-sender-modal';
import { MarkAsPaidModal } from './components/invoice-data-section/modals/mark-as-paid-modal';
import { MarkNotInvoiceModal } from './components/invoice-data-section/modals/mark-not-invoice-modal';
import { MarkNotPaidModal } from './components/invoice-data-section/modals/mark-not-paid-modal';
import { OAuthProviderDisconnectConfirmationModal } from './components/invoice-data-section/modals/oauth-provider-disconnect-confirmation-modal';
import { SettingsModal } from './components/invoice-data-section/modals/settings-modal';
import { XeroConnectModal } from './components/invoice-data-section/modals/xero/xero-connect-modal';
import { XeroConnectedModal } from './components/invoice-data-section/modals/xero/xero-connected-modal';
import { InvoiceDrawer } from './components/invoice-drawer/invoice-drawer';
import { InvoiceDrawerContentBodyVariant } from './components/invoice-drawer/invoice-drawer-content-body';
import { InvoicePayPage } from './invoice-pay-page';
import { getBeneficiaryCardConfig } from './utils/get-beneficiary-card-config';
import { getDateRangeValues } from './utils/get-date-range-values';
import { getEvidenceSuggestedTransactions } from './utils/get-evidence-suggested-transactions';
import { getFilteredInvoiceEvidence } from './utils/get-filtered-invoice-evidence';
import { getInReviewInvoices } from './utils/get-in-review-invoices';
import { getInvoiceMetrics } from './utils/get-invoice-metrics';
import { getLastPaidCardConfig } from './utils/get-last-paid-card-config';
import { useGmailConnection } from './utils/use-gmail-connection';
import { useXeroConnection } from './utils/use-xero-connection';

const POLLING_INTERVAL = 1000;
export const GMAIL_EXTRACTION_AFTER_CONNECTION_COMPLETED_KEY = 'is_gmail_extraction_after_connection_completed';
const GMAIL_EXTRACTION_AFTER_CONNECTION_COMPLETED_VALUE = 'true';
export const INVOICE_SCANNING_CARD_HIDDEN_KEY = 'invoice_scanning_card_hidden';
const INVOICE_SCANNING_CARD_HIDDEN_VALUE = 'true';
const XERO_SUCCESS_MODAL_SEEN_KEY = 'xero_success_modal_seen';
const XERO_SUCCESS_MODAL_SEEN_VALUE = 'true';
const XERO_FAILED_TOAST_SEEN_KEY = 'xero_failed_toast_seen';
const XERO_FAILED_TOAST_SEEN_VALUE = 'true';
const QUERY_PARAM_KEY_OAUTH_PROVIDER = 'provider';
const QUERY_PARAM_KEY_OAUTH_PROVIDER_CONNECTION_STATUS = 'success';
const QUERY_PARAM_VALUE_OAUTH_PROVIDER_XERO = 'xero';
const QUERY_PARAM_VALUE_OAUTH_PROVIDER_CONNECTION_STATUS_SUCCESS = 'true';
const QUERY_PARAM_VALUE_OAUTH_PROVIDER_CONNECTION_STATUS_FAILED = 'false';

const CONNECT_EMAIL_URL = '/api/oauth/authorize?provider=gmail&redirect=true';
export const CONNECT_XERO_URL = '/api/oauth/authorize?provider=xero&redirect=true';
const EMAIL_EXTRACTION_STATUS_URL = '/api/email/extraction-status';
const DOCUMENT_URL = '/api/documents';
const EVIDENCE_INVOICE_URL = '/api/evidence/invoice';
const EXCLUDE_SENDER_URL = '/api/email/connections';
const MARK_NOT_INVOICE_URL = '/api/evidence/invoice';
const APPROVE_INVOICE_URL = '/api/evidence/invoice/approve';
const CONNECTION_URL = '/api/connection/';

const PAYMENTS_URL = '/dashboard/payments';

type ClickedMenuItemVariantSource = 'ROW_MENU_ITEM' | 'DRAWER';

type EntryPoint = 'settings-modal' | 'invoices-page' | 'integrations-page';

export type DisconnectGmailResponse = {
  success: boolean;
  message?: string;
};

// These are the workflow-status statues enum values
// server/src/models/workflow-status.ts
// only these 3 are used server/src/controllers/email-controller/email-controller.ts
type EmailExtractionStatus = 'running' | 'completed' | 'failed';

type EmailExtractionStatusResponse = {
  status: EmailExtractionStatus;
  totalInvoiceCount: number;
};

type MarkAsPaidPayload = {
  isPaid: boolean;
  transactionId?: string;
};

type ExcludeSenderPayload = {
  excludedSenders: string[];
  // Source: https://linear.app/seapoint/issue/TENG-1898/dont-read-invoices-from-this-sender#comment-b0a07ddd
  mode?: 'append' | 'replace';
};

type ExcludeSenderResponse = {
  success: boolean;
  excludedSenders: string[];
  deletedEvidenceIds: string[];
};

type EmailConnectionStatusResponse = { connections: { excludedSenders: string[] }[]; totalCount: number };

export const InvoicePayPageInnerWrapper = () => {
  const { isReviewMatchLive } = useFlags();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, isLoading: isLoadingData, error: dataError, mutations } = useData();
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const isEmailModalOpen = searchParams.get('email-modal-open') === 'true';
  const [isEmailConnectionModalOpen, setIsEmailConnectionModalOpen] = useState<
    | { isOpen: false }
    | {
        isOpen: true;
        entryPoint: EntryPoint;
      }
  >(isEmailModalOpen ? { isOpen: true, entryPoint: 'integrations-page' } : { isOpen: false });
  const [isXeroConnectionModalOpen, setIsXeroConnectionModalOpen] = useState<
    | { isOpen: false }
    | {
        isOpen: true;
        entryPoint: Exclude<EntryPoint, 'integrations-page'>;
      }
  >({
    isOpen: false,
  });
  const [isEmailDisconnectConfirmationModalOpen, setIsEmailDisconnectConfirmationModalOpen] = useState(false);
  const [isXeroDisconnectConfirmationModalOpen, setIsXeroDisconnectConfirmationModalOpen] = useState(false);
  const [isMenuItemModalOpen, setIsMenuItemModalOpen] = useState(false);
  const [invoiceDrawerConfig, setInvoiceDrawerConfig] = useState<
    { isOpen: false } | { isOpen: true; variant: InvoiceDrawerContentBodyVariant }
  >({
    isOpen: false,
  });
  const [clickedMenuItemVariant, setClickedMenuItemVariant] = useState<
    { menuItem: MenuItemVariant; source: ClickedMenuItemVariantSource } | undefined
  >(undefined);
  const [selectedPaidTransactionId, setSelectedPaidTransactionId] = useState<string | undefined>(undefined);
  const [isMarkingAsPaid, setIsMarkingAsPaid] = useState(false);
  const [isMatchingToTransaction, setIsMatchingToTransaction] = useState(false);
  const [isScanningCardVisible, setIsScanningCardVisible] = useState(
    localStorage.getItem(INVOICE_SCANNING_CARD_HIDDEN_KEY) !== INVOICE_SCANNING_CARD_HIDDEN_VALUE,
  );
  const [tableSortedEvidence, setTableSortedEvidence] = useState<InvoiceEvidenceView[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const {
    xeroConnectionStatus,
    isCheckingXeroConnection,
    isDisconnectingXero,
    mutations: { onDisconnectXero },
  } = useXeroConnection();
  const {
    gmailConnectionStatus: connectionStatus,
    isCheckingGmailConnection: isCheckingConnection,
    isDisconnectingGmail: isDisconnectingEmail,
    mutations: { onDisconnectGmail: onDisconnectEmail },
  } = useGmailConnection();
  const isGmailExtractionAfterConnectionCompleted = localStorage.getItem(
    GMAIL_EXTRACTION_AFTER_CONNECTION_COMPLETED_KEY,
  );

  const isXeroConnectionProvider =
    searchParams.get(QUERY_PARAM_KEY_OAUTH_PROVIDER) === QUERY_PARAM_VALUE_OAUTH_PROVIDER_XERO;
  const isConnectionSuccess =
    searchParams.get(QUERY_PARAM_KEY_OAUTH_PROVIDER_CONNECTION_STATUS) ===
    QUERY_PARAM_VALUE_OAUTH_PROVIDER_CONNECTION_STATUS_SUCCESS;
  const isConnectionFailed =
    searchParams.get(QUERY_PARAM_KEY_OAUTH_PROVIDER_CONNECTION_STATUS) ===
    QUERY_PARAM_VALUE_OAUTH_PROVIDER_CONNECTION_STATUS_FAILED;

  const isXeroSuccessModalSeen = localStorage.getItem(XERO_SUCCESS_MODAL_SEEN_KEY) === XERO_SUCCESS_MODAL_SEEN_VALUE;

  const [isXeroConnectionSuccessModalOpen, setIsXeroConnectionSuccessModalOpen] = useState(
    isXeroConnectionProvider && isConnectionSuccess && !isXeroSuccessModalSeen,
  );
  const { data: emailProgressStatus } = useSWRImmutable<EmailExtractionStatusResponse, Error, string | null>(
    connectionStatus?.connected && isScanningCardVisible
      ? `${EMAIL_EXTRACTION_STATUS_URL}?connectionId=${connectionStatus.connectionId}`
      : null,
    getSwrFetcher,
    {
      refreshInterval: () => {
        if (isGmailExtractionAfterConnectionCompleted !== GMAIL_EXTRACTION_AFTER_CONNECTION_COMPLETED_VALUE) {
          return POLLING_INTERVAL;
        }

        return 0;
      },
      refreshWhenHidden: true,
      onSuccess: async (newDate) => {
        if (newDate.status !== 'completed' && newDate.status !== 'failed') {
          return;
        }

        localStorage.setItem(
          GMAIL_EXTRACTION_AFTER_CONNECTION_COMPLETED_KEY,
          GMAIL_EXTRACTION_AFTER_CONNECTION_COMPLETED_VALUE,
        );

        await mutations.handleIncrementalUpdates();
      },
      onError: (error) => {
        Sentry.captureMessage(error.message, {
          extra: {
            connectionId: connectionStatus?.connectionId,
            message: 'Failed to fetch email extraction status',
          },
        });

        localStorage.removeItem(GMAIL_EXTRACTION_AFTER_CONNECTION_COMPLETED_KEY);
        toast.error('There was a problem checking your email connection status');
      },
    },
  );

  const {
    trigger: triggerDocumentUrl,
    isMutating: isTriggeringDocumentUrl,
    data: documentUrl,
  } = useSWRMutation<{ url: string }, Error, string, { urlSuffix: string }>(DOCUMENT_URL, getSwrFetcherWithDynamicUrl);
  const [selectedEvidenceId, setSelectedEvidenceId] = useState<string | undefined>(undefined);
  const { trigger: triggerIsPaid, isMutating: isTriggeringIsPaid } = useSWRMutation<
    InvoiceEvidenceView,
    Error,
    string,
    MarkAsPaidPayload & { urlSuffix: string }
  >(EVIDENCE_INVOICE_URL, postSwrFetcherWithDynamicUrl);
  const { trigger: triggerExcludeSender, isMutating: isExcludingSender } = useSWRMutation<
    ExcludeSenderResponse,
    Error,
    string,
    ExcludeSenderPayload & { urlSuffix: string }
  >(EXCLUDE_SENDER_URL, putSwrFetcherWithDynamicUrl);
  const { trigger: triggerMarkNotInvoice, isMutating: isMarkingNotInvoice } = useSWRMutation<
    { message: string },
    Error,
    string,
    { urlSuffix: string }
  >(MARK_NOT_INVOICE_URL, deleteSwrFetcherWithDynamicUrl);
  const {
    data: emailConnectionStatus,
    isLoading: isLoadingEmailConnectionStatus,
    error: emailConnectionStatusError,
    mutate: mutateEmailConnectionStatus,
  } = useSWR<EmailConnectionStatusResponse, Error, string>(CONNECTION_URL);
  const { trigger: triggerApproveInvoices } = useSWRMutation<
    { message: string },
    Error,
    string,
    { invoiceIds: string[] }
  >(APPROVE_INVOICE_URL, postSwrFetcher);
  const [approvingEvidenceId, setApprovingEvidenceId] = useState<string[] | undefined>(undefined);

  const { selectedId, mutations: modalMutations } = useEntityModalState<TransactionModalView>({
    defaultView: 'transaction',
  });

  useEffect(() => {
    if (
      isXeroConnectionProvider &&
      isConnectionFailed &&
      localStorage.getItem(XERO_FAILED_TOAST_SEEN_KEY) !== XERO_FAILED_TOAST_SEEN_VALUE
    ) {
      localStorage.setItem(XERO_FAILED_TOAST_SEEN_KEY, 'true');

      toast.error('Failed to connect to Xero. Please try again and contact us if the issue persists.');
    }
  }, [isConnectionFailed, isXeroConnectionProvider]);

  if (isLoadingData || isLoadingEmailConnectionStatus) {
    return <LoadingSection text="Loading..." />;
  }

  if (!data || !emailConnectionStatus) {
    return <ErrorSection error={dataError || emailConnectionStatusError} />;
  }

  const { transactions, evidence, baseCurrency, documents, categories } = data;

  const sortedEvidencesTransactions = tableSortedEvidence.map((e) => e.transaction).filter((t) => t !== undefined);
  const selectedTransaction = selectedId
    ? sortedEvidencesTransactions.find((transaction) => transaction.id === selectedId) || null
    : null;

  const redirectUrlQueryParam = `redirectUrl=${encodeURIComponent(window.location.pathname)}`;

  const onConnectEmail = async (
    values: Parameters<ComponentProps<typeof EmailConnectionModal>['onSelectDateRange']>[0],
  ) => {
    setIsLoading(true);

    const dateRangeValues = getDateRangeValues(values.dateRange);

    if (!dateRangeValues) {
      window.location.href = `${CONNECT_EMAIL_URL}&${redirectUrlQueryParam}`;

      return;
    }

    const { startDate, endDate } = dateRangeValues;

    window.location.href = `${CONNECT_EMAIL_URL}&startDate=${startDate}&endDate=${endDate}&${redirectUrlQueryParam}`;
  };

  const onConnectXero = async () => {
    window.location.href = `${CONNECT_XERO_URL}&${redirectUrlQueryParam}`;

    return;
  };

  const onProceedToPayment = async () => {
    const selectedEvidence = evidence.find((e) => e.id === selectedEvidenceId);

    if (!selectedEvidence) {
      Sentry.captureMessage('Selected evidence not found in InvoicePayPageInnerWrapper', {
        extra: {
          selectedEvidenceId,
          evidenceIds: evidence.map((e) => e.id),
          message: 'Selected evidence not found in InvoicePayPageInnerWrapper',
        },
      });

      return;
    }

    if (!selectedEvidence.document) {
      Sentry.captureMessage('Selected evidence has no document linked in InvoicePayPageInnerWrapper', {
        extra: {
          selectedEvidenceId,
          evidenceIds: evidence.map((e) => e.id),
          document: selectedEvidence.document,
          documentId: selectedEvidence.documentId,
          documentIds: documents.map((d) => d.id),
          message: 'Selected evidence has no document linked in InvoicePayPageInnerWrapper',
        },
      });

      return;
    }

    if (!selectedEvidence.documentId) {
      Sentry.captureMessage('Selected evidence has no document id', {
        extra: {
          selectedEvidenceId,
          evidenceIds: evidence.map((e) => e.id),
          message: 'Selected evidence has no document id',
        },
      });
    }

    try {
      const result = await triggerDocumentUrl({ urlSuffix: `/${selectedEvidence.documentId}/signed-url` });

      navigate(`${PAYMENTS_URL}?action=BILL_PAY`, {
        state: {
          isFromToPayRowReviewInvoicePageConfig: {
            isFromToPayRowReviewInvoicePage: true,
            invoiceId: selectedEvidenceId,
            invoiceReference: selectedEvidence.invoiceDetails?.invoiceReference,
            fileName: selectedEvidence.document.fileName,
            documentUrl: result,
          },
        },
      });

      return;
    } catch (error) {
      Sentry.captureException(error);

      setSelectedEvidenceId(undefined);
      toast.error('Failed to fetch invoice details');
    }
  };

  const resetMenuItemState = () => {
    setIsMenuItemModalOpen(false);
    setClickedMenuItemVariant(undefined);

    if (clickedMenuItemVariant?.source === 'ROW_MENU_ITEM') {
      setSelectedEvidenceId(undefined);
    }

    setSelectedPaidTransactionId(undefined);
  };

  const onHideCard = () => {
    localStorage.setItem(INVOICE_SCANNING_CARD_HIDDEN_KEY, INVOICE_SCANNING_CARD_HIDDEN_VALUE);
    setIsScanningCardVisible(false);
  };

  const emailScanningProgressConfig = getEmailScanningProgressStatus({ emailProgressStatus, onHideCard });
  const invoiceEvidences = getFilteredInvoiceEvidence(evidence as InvoiceEvidenceView[], isReviewMatchLive);
  const invoicesMetrics = getInvoiceMetrics({ evidences: invoiceEvidences, baseCurrency });

  const unpaidInvoices = invoiceEvidences.filter((e) => e.isPaid === false);
  const inReviewInvoices = getInReviewInvoices(invoiceEvidences);
  const completedInvoices = isReviewMatchLive
    ? invoiceEvidences.filter((e) => e.status === EvidenceStatus.COMPLETED)
    : invoiceEvidences.filter((e) => e.isPaid === true);

  const selectedEvidence = invoiceEvidences.find((e) => e.id === selectedEvidenceId);
  const selectedEvidenceSuggestedTransactions = getEvidenceSuggestedTransactions(selectedEvidence);
  const suggestedTransactions = transactions.filter((t) => selectedEvidenceSuggestedTransactions?.includes(t.id));

  const emailConnectionConfig = connectionStatus?.connectionId
    ? {
        isConnected: true as const,
        email: connectionStatus.email ?? 'Gmail Connection Active',
        onDisconnect: async () => {
          setIsSettingsModalOpen(false);

          setIsEmailDisconnectConfirmationModalOpen(true);
        },
      }
    : {
        isConnected: false as const,
        onConnect: () => {
          setIsSettingsModalOpen(false);

          setIsEmailConnectionModalOpen({ isOpen: true, entryPoint: 'settings-modal' });
        },
      };

  const xeroConnectionConfig = xeroConnectionStatus?.connectionId
    ? {
        isConnected: true as const,
        email: xeroConnectionStatus.email ?? 'Xero Connection Active',
        onDisconnect: async () => {
          setIsSettingsModalOpen(false);

          setIsXeroDisconnectConfirmationModalOpen(true);

          localStorage.removeItem(XERO_SUCCESS_MODAL_SEEN_KEY);
          localStorage.removeItem(IS_XERO_CARD_HIDDEN_KEY);
        },
      }
    : {
        isConnected: false as const,
        onConnect: () => {
          setIsSettingsModalOpen(false);

          setIsXeroConnectionModalOpen({ isOpen: true, entryPoint: 'settings-modal' });
        },
      };

  const onApproveInvoice = async (invoiceIds: string[]) => {
    try {
      setApprovingEvidenceId(invoiceIds);

      await triggerApproveInvoices({ invoiceIds });

      if (invoiceIds.length === 1) {
        toast.success('Invoice approved');
      } else {
        toast.success('Invoices approved');
      }

      await mutations.handleIncrementalUpdates();
    } catch (error) {
      Sentry.captureException(error);

      if (invoiceIds.length === 1) {
        toast.error('Failed to approve invoice. Please try again.');
      } else {
        toast.error('Failed to approve invoices. Please try again.');
      }

      toast.error('Failed to approve invoice. Please try again.');
    } finally {
      setApprovingEvidenceId(undefined);
    }
  };

  const onToggleSelectedTransaction = (transactionId: string) => {
    if (selectedPaidTransactionId) {
      setSelectedPaidTransactionId(undefined);

      return;
    }

    setSelectedPaidTransactionId(transactionId);
  };

  return (
    <>
      <InvoicePayPage
        evidenceDataConfig={{
          unpaidInvoices,
          inReviewInvoices,
          completedInvoices,
        }}
        emailConnectionConfig={{
          provider: OAuthProvider.GMAIL,
          isConnected: !!connectionStatus?.connectionId,
          isLoading: isDisconnectingEmail || isCheckingConnection || isLoading,
          onConnect: () => setIsEmailConnectionModalOpen({ isOpen: true, entryPoint: 'invoices-page' }),
        }}
        xeroConnectionConfig={{
          provider: OAuthProvider.XERO,
          isConnected: !!xeroConnectionStatus?.connectionId,
          isLoading: isCheckingXeroConnection,
          onConnect: () => {
            localStorage.removeItem(XERO_FAILED_TOAST_SEEN_KEY);

            setIsXeroConnectionModalOpen({ isOpen: true, entryPoint: 'invoices-page' });
          },
        }}
        invoicesMetrics={invoicesMetrics}
        emailScanningProgressConfig={emailScanningProgressConfig}
        isFetchingDocumentUrl={isTriggeringDocumentUrl}
        selectedEvidenceId={selectedEvidenceId}
        onUploadManualInvoice={() =>
          navigate(PAYMENTS_URL, {
            state: {
              isFromManualPayInvoicePage: true,
              action: 'BILL_PAY_UPLOAD_INVOICE',
            },
          })
        }
        onReviewInvoice={async (invoiceId, variant) => {
          const selectedEvidence = evidence.find((e) => e.id === invoiceId);

          if (!selectedEvidence || !selectedEvidence.document) {
            Sentry.captureMessage('Selected evidence not found on onReviewInvoice in InvoicePayPageInnerWrapper', {
              extra: {
                selectedEvidenceId,
                evidenceIds: evidence.map((e) => e.id),
                message: 'Selected evidence not found on onReviewInvoice in InvoicePayPageInnerWrapper',
              },
            });

            return;
          }

          try {
            await triggerDocumentUrl({ urlSuffix: `/${selectedEvidence.documentId}/signed-url` });

            setInvoiceDrawerConfig({ isOpen: true, variant });

            setSelectedEvidenceId(invoiceId);
          } catch (error) {
            Sentry.captureException(error);

            toast.error('Failed to load invoice details');
          }
        }}
        onSelectMenuItem={(payload) => {
          setIsMenuItemModalOpen(true);
          setSelectedEvidenceId(payload.entityId);
          setClickedMenuItemVariant({ menuItem: payload.menuItemVariant as MenuItemVariant, source: 'ROW_MENU_ITEM' });
        }}
        onClickSettings={() => setIsSettingsModalOpen(true)}
        onDataSorted={setTableSortedEvidence}
        onApproveInvoice={onApproveInvoice}
        approvingEvidenceId={approvingEvidenceId}
        onReviewTransaction={modalMutations.changeSelectedItem}
      />

      <SettingsModal
        isOpen={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
        excludedSendersConfig={{
          isLoading: isExcludingSender,
          excludedSenders: emailConnectionStatus.connections[0]?.excludedSenders ?? [],
          onSubmit: async (excludedSenders) => {
            if (!connectionStatus?.connectionId) {
              Sentry.captureMessage('No connection id found when trying to exclude sender list', {
                extra: {
                  connectionStatus,
                },
              });

              return;
            }

            try {
              const { deletedEvidenceIds } = await triggerExcludeSender({
                urlSuffix: `/${connectionStatus.connectionId}/excluded-senders`,
                excludedSenders,
                mode: 'replace',
              });

              await mutateEmailConnectionStatus();
              await mutations.handleDeleteUpdates({
                entityType: 'evidence',
                entityIds: deletedEvidenceIds,
              });
              resetMenuItemState();

              toast.success('Sender excluded', {
                description: `We won't pull in any more invoices from these email addresses.`,
              });
            } catch (error) {
              Sentry.captureException(error);

              toast.error('Failed to exclude senders');
            }
          },
        }}
        emailConnectionConfig={emailConnectionConfig}
        xeroConnectionConfig={xeroConnectionConfig}
      />

      <EmailConnectionModal
        isOpen={isEmailConnectionModalOpen.isOpen}
        onClose={() => {
          setIsEmailConnectionModalOpen({ isOpen: false });

          if (isEmailConnectionModalOpen.isOpen && isEmailConnectionModalOpen.entryPoint === 'settings-modal') {
            setIsSettingsModalOpen(true);
          }

          if (isEmailConnectionModalOpen.isOpen && isEmailConnectionModalOpen.entryPoint === 'integrations-page') {
            navigate('/dashboard/integrations');
          }
        }}
        onSelectDateRange={onConnectEmail}
      />

      <XeroConnectModal
        isOpen={isXeroConnectionModalOpen.isOpen}
        onClose={() => setIsXeroConnectionModalOpen({ isOpen: false })}
        onContinue={onConnectXero}
      />

      <XeroConnectedModal
        isOpen={isXeroConnectionSuccessModalOpen}
        onClose={() => {
          setIsXeroConnectionSuccessModalOpen(false);

          localStorage.setItem(XERO_SUCCESS_MODAL_SEEN_KEY, XERO_SUCCESS_MODAL_SEEN_VALUE);
        }}
      />

      <OAuthProviderDisconnectConfirmationModal
        provider={OAuthProvider.GMAIL}
        isOpen={isEmailDisconnectConfirmationModalOpen}
        isLoading={isDisconnectingEmail}
        onClose={() => {
          setIsEmailDisconnectConfirmationModalOpen(false);

          setIsSettingsModalOpen(true);
        }}
        onDisconnect={async () => {
          if (!connectionStatus?.connectionId) {
            Sentry.captureMessage('No connection id found when trying to disconnect email', {
              extra: {
                connectionStatus,
              },
            });

            return;
          }

          await onDisconnectEmail(connectionStatus.connectionId);

          setIsEmailDisconnectConfirmationModalOpen(false);
        }}
      />

      <OAuthProviderDisconnectConfirmationModal
        provider={OAuthProvider.XERO}
        isOpen={isXeroDisconnectConfirmationModalOpen}
        isLoading={isDisconnectingXero}
        onClose={() => {
          setIsXeroDisconnectConfirmationModalOpen(false);

          setIsSettingsModalOpen(true);
        }}
        onDisconnect={async () => {
          if (!xeroConnectionStatus?.connectionId) {
            Sentry.captureMessage('No connection id found when trying to disconnect xero', {
              extra: {
                xeroConnectionStatus,
              },
            });

            return;
          }

          await onDisconnectXero(xeroConnectionStatus.connectionId);

          setIsXeroDisconnectConfirmationModalOpen(false);
        }}
      />

      {clickedMenuItemVariant &&
        ['MARK_PAID', 'CHANGE_TRANSACTION_MATCH', 'MATCH_TO_DIFFERENT_TRANSACTION'].includes(
          clickedMenuItemVariant.menuItem,
        ) &&
        selectedEvidence && (
          <MarkAsPaidModal
            isOpen={isMenuItemModalOpen}
            onClose={() => {
              resetMenuItemState();
              // In case user paginated in the modal table
              searchParams.delete('p');

              setSearchParams(searchParams);
            }}
            transactions={suggestedTransactions.length > 0 ? suggestedTransactions : transactions}
            selectedPaidTransactionId={selectedPaidTransactionId}
            canMarkAsPaidWithoutMatching={clickedMenuItemVariant?.menuItem === 'MARK_PAID'}
            isMatching={isMatchingToTransaction}
            isMarkingAsPaid={isMarkingAsPaid}
            onToggleTransaction={onToggleSelectedTransaction}
            onMatchToTransaction={async () => {
              if (!selectedPaidTransactionId) {
                return;
              }

              setIsMatchingToTransaction(true);

              try {
                await triggerIsPaid({
                  urlSuffix: `/${selectedEvidence.id}/is-paid`,
                  isPaid: true,
                  transactionId: selectedPaidTransactionId,
                });

                await mutations.handleIncrementalUpdates();

                resetMenuItemState();

                if (clickedMenuItemVariant?.menuItem === 'MARK_PAID') {
                  toast.success('Invoice marked as paid', {
                    description: `We've removed it from your payment queue and updated your records.`,
                  });

                  return;
                }

                toast.success('Transaction match changed', {
                  description: `We've updated your records.`,
                });
              } catch (error) {
                Sentry.captureException(error);

                toast.error('Failed to mark this invoice as paid');
              } finally {
                setIsMatchingToTransaction(false);
              }
            }}
            onMarkAsPaidWithoutMatching={async () => {
              setIsMarkingAsPaid(true);

              try {
                await triggerIsPaid({
                  urlSuffix: `/${selectedEvidence.id}/is-paid`,
                  isPaid: true,
                });

                await mutations.handleIncrementalUpdates();

                resetMenuItemState();

                if (clickedMenuItemVariant?.menuItem === 'MARK_PAID') {
                  toast.success('Invoice marked as paid', {
                    description: `We've removed it from your payment queue and updated your records.`,
                  });

                  return;
                }

                toast.success('Transaction match changed', {
                  description: `We've updated your records.`,
                });
              } catch (error) {
                Sentry.captureException(error);

                toast.error('Failed to mark this invoice as paid');
              } finally {
                setIsMarkingAsPaid(false);
              }
            }}
          />
        )}

      {clickedMenuItemVariant?.menuItem === 'EXCLUDE_SENDER' && selectedEvidence && (
        <ExcludeSenderModal
          isOpen={isMenuItemModalOpen}
          onClose={resetMenuItemState}
          invoiceInfo={getInvoiceInfo(selectedEvidence)}
          isLoading={isExcludingSender}
          onExcludeSender={async () => {
            if (!selectedEvidence) {
              Sentry.captureMessage('No selected evidence found when trying to exclude sender', {
                extra: {
                  evidenceIds: evidence.map((e) => e.id),
                  message: 'No selected evidence found when trying to exclude sender',
                },
              });

              return;
            }

            if (!connectionStatus?.connectionId) {
              Sentry.captureMessage('No connection id found when trying to exclude sender', {
                extra: {
                  selectedEvidenceId: selectedEvidence.id,
                  connectionStatus,
                  message: 'No connection id found when trying to exclude sender',
                },
              });

              return;
            }

            if (!selectedEvidence.emailInformation?.fromEmail) {
              Sentry.captureMessage('No from email found when trying to exclude sender', {
                extra: {
                  selectedEvidenceId: selectedEvidence.id,
                  evidenceEmailInformation: selectedEvidence.emailInformation,
                  message: 'No from email found when trying to exclude sender',
                },
              });

              return;
            }

            const excludedSenders: string[] = [];
            excludedSenders.push(selectedEvidence.emailInformation.fromEmail);

            try {
              const { deletedEvidenceIds } = await triggerExcludeSender({
                urlSuffix: `/${connectionStatus.connectionId}/excluded-senders`,
                excludedSenders,
              });

              await mutateEmailConnectionStatus();
              await mutations.handleDeleteUpdates({
                entityType: 'evidence',
                entityIds: deletedEvidenceIds,
              });

              resetMenuItemState();

              toast.success('Sender excluded', {
                description: `We won't pull in any more invoices from this email address.`,
              });
            } catch (error) {
              Sentry.captureException(error);

              toast.error('Failed to exclude sender');
            }
          }}
        />
      )}

      {clickedMenuItemVariant &&
        ['MARK_NOT_INVOICE', 'DELETE_INVOICE'].includes(clickedMenuItemVariant.menuItem) &&
        selectedEvidence && (
          <MarkNotInvoiceModal
            isOpen={isMenuItemModalOpen}
            invoiceInfo={getInvoiceInfo(selectedEvidence)}
            isLoading={isMarkingNotInvoice}
            onClose={resetMenuItemState}
            onMarkNotInvoice={async () => {
              if (!selectedEvidence) {
                Sentry.captureMessage('No selected evidence found when trying to mark not invoice', {
                  extra: {
                    evidenceIds: evidence.map((e) => e.id),
                    message: 'No selected evidence found when trying to mark not invoice',
                  },
                });

                return;
              }

              try {
                await triggerMarkNotInvoice({ urlSuffix: `/${selectedEvidence.id}` });

                await mutations.handleDeleteUpdates({
                  entityType: 'evidence',
                  entityIds: [selectedEvidence.id],
                });

                resetMenuItemState();

                toast.success('Marked as not an invoice', {
                  description: `We've removed it from your list to keep things accurate.`,
                });
              } catch (error) {
                Sentry.captureException(error);

                toast.error('Failed to mark this invoice as not invoice');
              }
            }}
          />
        )}

      {clickedMenuItemVariant &&
        ['MARK_NOT_PAID', 'I_HAVEN_T_PAID_THIS_INVOICE_YET'].includes(clickedMenuItemVariant.menuItem) &&
        selectedEvidence && (
          <MarkNotPaidModal
            isOpen={isMenuItemModalOpen}
            invoiceInfo={getInvoiceInfo(selectedEvidence)}
            isLoading={isTriggeringIsPaid}
            onClose={resetMenuItemState}
            onMarkNotPaid={async () => {
              try {
                await triggerIsPaid({
                  urlSuffix: `/${selectedEvidence.id}/is-paid`,
                  isPaid: false,
                });

                resetMenuItemState();

                await mutations.handleIncrementalUpdates();

                toast.success('Marked as unpaid', {
                  description: `We've moved it back to your invoice queue so you can review and pay it.`,
                });
              } catch (error) {
                Sentry.captureException(error);

                toast.error('Failed to mark this invoice as unpaid');
              }
            }}
          />
        )}

      {selectedEvidence && documentUrl && (
        <InvoiceDrawer
          isOpen={invoiceDrawerConfig.isOpen}
          onClose={() => setInvoiceDrawerConfig({ isOpen: false })}
          invoiceDrawerContentConfig={{
            invoiceVendor:
              selectedEvidence.suggestedBeneficiary?.name ??
              selectedEvidence.vendorInformation?.vendorCompanyName ??
              'Unknown',
            pdfConfig: {
              url: documentUrl.url,
            },
            navigationConfig: {
              isFirstInvoice: getEvidenceIndex(tableSortedEvidence, selectedEvidence) === 0,
              isLastInvoice: getEvidenceIndex(tableSortedEvidence, selectedEvidence) === tableSortedEvidence.length - 1,
              onPrevious: async () => {
                const currentIndex = getEvidenceIndex(tableSortedEvidence, selectedEvidence);

                if (currentIndex === 0) {
                  return;
                }

                const previousEvidence = tableSortedEvidence[currentIndex - 1];

                await triggerDocumentUrl({ urlSuffix: `/${previousEvidence.documentId}/signed-url` });

                setSelectedEvidenceId(previousEvidence.id);
              },
              onNext: async () => {
                const currentIndex = getEvidenceIndex(tableSortedEvidence, selectedEvidence);

                if (currentIndex === tableSortedEvidence.length - 1) {
                  return;
                }

                const nextEvidence = tableSortedEvidence[currentIndex + 1];

                await triggerDocumentUrl({ urlSuffix: `/${nextEvidence.documentId}/signed-url` });

                setSelectedEvidenceId(nextEvidence.id);
              },
            },
            contentBodyConfig:
              invoiceDrawerConfig.isOpen && invoiceDrawerConfig.variant === 'TO_PAY'
                ? {
                    variant: 'TO_PAY',
                    isDismissingInvoice: isMarkingNotInvoice || isExcludingSender,
                    lastPaidCardConfig: getLastPaidCardConfig({
                      suggestedBeneficiaryId: selectedEvidence.suggestedBeneficiary?.id,
                      transactions,
                    }),
                    beneficiaryCardConfig: getBeneficiaryCardConfig(selectedEvidence),
                    summaryConfig:
                      selectedEvidence.summary || selectedEvidence.emailInformation?.fromEmail
                        ? {
                            isVisible: true,
                            summary: selectedEvidence.summary,
                            fromEmail: selectedEvidence.emailInformation?.fromEmail,
                            onMarkAsPaid: () => {
                              setIsMenuItemModalOpen(true);
                              setClickedMenuItemVariant({ menuItem: 'MARK_PAID', source: 'DRAWER' });
                            },
                          }
                        : {
                            isVisible: false,
                            onMarkAsPaid: () => {
                              setIsMenuItemModalOpen(true);
                              setClickedMenuItemVariant({ menuItem: 'MARK_PAID', source: 'DRAWER' });
                            },
                          },
                    onProceedToPayment,
                    onExcludeSender: () => {
                      setIsMenuItemModalOpen(true);
                      setClickedMenuItemVariant({ menuItem: 'EXCLUDE_SENDER', source: 'DRAWER' });
                    },
                  }
                : {
                    variant: 'REVIEW_MATCHES',
                    confidenceScore: selectedEvidence.isPaidConfidenceScore ?? 0,
                    hasMatchedTransaction: !!selectedEvidence.transactionId,
                    cardConfig: {
                      name:
                        selectedEvidence.suggestedBeneficiary?.name ??
                        selectedEvidence.vendorInformation?.vendorCompanyName ??
                        'Unknown',
                      date: selectedEvidence.transaction?.fspDate
                        ? format(new UTCDate(selectedEvidence.transaction.fspDate), 'dd MMM')
                        : 'Unknown',
                      reference:
                        selectedEvidence.transaction?.type === TransactionType.TRANSFER ||
                        selectedEvidence.transaction?.type === TransactionType.DIRECT_DEBIT
                          ? selectedEvidence.transaction?.reference
                          : null,
                      formattedAmount:
                        !selectedEvidence.transaction?.amount ||
                        !selectedEvidence.transaction?.currency ||
                        !isSupportedCurrency(selectedEvidence.transaction?.currency)
                          ? '-'
                          : getAmountWithCurrency({
                              amount: selectedEvidence.transaction?.amount,
                              currency: selectedEvidence.transaction?.currency,
                              isAccountBalance: true,
                            }),
                      onChangeMatchedTransaction: () => {
                        setIsMenuItemModalOpen(true);
                        setClickedMenuItemVariant({
                          menuItem: 'MATCH_TO_DIFFERENT_TRANSACTION' as const,
                          source: 'DRAWER',
                        });
                      },
                      onRejectInvoice: () => {
                        setIsMenuItemModalOpen(true);
                        setClickedMenuItemVariant({ menuItem: 'MARK_NOT_INVOICE' as const, source: 'DRAWER' });
                      },
                    },
                    isApproveMatchLoading: !!approvingEvidenceId,
                    onApproveMatch: () => onApproveInvoice([selectedEvidence.id]),
                  },
            onMarkNotInvoice: () => {
              setIsMenuItemModalOpen(true);
              setClickedMenuItemVariant({ menuItem: 'MARK_NOT_INVOICE', source: 'DRAWER' });
            },
          }}
        />
      )}

      <TransactionDetailsDrawerWrapper
        selectedTransaction={selectedTransaction}
        transactions={sortedEvidencesTransactions}
        categories={categories}
        onCloseDrawer={modalMutations.resetSearchParams}
        onChangeSelectedTransaction={modalMutations.changeSelectedItem}
      />
    </>
  );
};

type GetEmailScanningProgressStatusProps = {
  emailProgressStatus?: EmailExtractionStatusResponse;
  onHideCard: () => void;
};

const getEmailScanningProgressStatus = ({
  emailProgressStatus,
  onHideCard,
}: GetEmailScanningProgressStatusProps): ComponentProps<typeof InvoicePayPage>['emailScanningProgressConfig'] => {
  if (
    !emailProgressStatus ||
    localStorage.getItem(INVOICE_SCANNING_CARD_HIDDEN_KEY) === INVOICE_SCANNING_CARD_HIDDEN_VALUE
  ) {
    return { isVisible: false };
  }

  const { status, totalInvoiceCount } = emailProgressStatus;

  switch (status) {
    case 'failed':
      return { isVisible: true, status: 'FAILED', invoiceCount: totalInvoiceCount, onHideCard };
    case 'running':
      return {
        isVisible: true,
        status: 'EXTRACTING',
        invoiceCount: totalInvoiceCount,
        onHideCard,
      };
    case 'completed':
      return {
        isVisible: true,
        status: 'COMPLETE',
        invoiceCount: totalInvoiceCount,
        onHideCard,
      };
    default:
      return exhaustiveCheck(status);
  }
};

const getInvoiceInfo = (evidence: InvoiceEvidenceView) => {
  return {
    issueDate: evidence.emailInformation?.time,
    vendorName: evidence.suggestedBeneficiary?.name ?? evidence.vendorInformation?.vendorCompanyName ?? 'Unknown',
    invoiceReference: evidence.invoiceDetails?.invoiceReference ?? 'Unknown',
    amount: evidence.invoiceDetails?.totalAmount,
    currency: evidence.invoiceDetails?.currency,
  };
};

const getEvidenceIndex = (evidenceList: InvoiceEvidenceView[], selectedEvidence: InvoiceEvidenceView) => {
  return evidenceList.map((e) => e.id).indexOf(selectedEvidence.id);
};
